import { AutoCompleteIcons, getKeyWords, getKeyWordsHighlight } from '../ShellyScriptHelpers';
import ShellyScriptHightlight from '../ShellyScriptHightlight';
import { shellyKeyWords, jsKeyWords } from '../docs/js-script';
import type { AutoCompleteOption } from '../ShellyScriptHelpers';

export default class ShellyScriptAutoComplete extends EventTarget {
	private _textEditor: HTMLTextAreaElement;
	private _mirrorEditor: HTMLDivElement;
	private _autocompleteMenu: HTMLElement;
	private _position = { x: 0, y: 0, offsetX: 0, offsetY: 0, lineHeight: 0 };
	constructor(editor: HTMLTextAreaElement) {
		super();
		this._textEditor = editor;
		this._autocompleteMenu = document.createElement('ul');
		this._autocompleteMenu.className = 'shelly-script-editor-autocomplete-menu';
		this._textEditor.parentNode.appendChild(this._autocompleteMenu);
		this._mirrorEditor = document.createElement('div');
		this._mirrorEditor.className = 'mirror-autocomplete';
		this._textEditor.parentNode.prepend(this._mirrorEditor);
		this._mirrorEditor.style.visibility = 'hidden';
		const style = getComputedStyle(this._textEditor);
		this._position.lineHeight = parseInt(style.lineHeight);
		this._textEditor.addEventListener('keydown', this._handleKeyDown, true);
		this._textEditor.addEventListener('keyup', this._handleKeyUp);
	}
	public setPosition(x: number, y: number) {
		this._position.x = x;
		this._position.y = y;
		this._updateCaretPosition();
	}
	public destroy() {
		this._textEditor.removeEventListener('keydown', this._handleKeyDown, true);
		this._textEditor.removeEventListener('keyup', this._handleKeyUp);
		this._textEditor.parentNode.removeChild(this._autocompleteMenu);
		window.removeEventListener('resize', this._updateCaretPosition);
	}
	private _handleKeyDown = (e: KeyboardEvent) => {
		if (
			this._autocompleteMenu.className.includes('show') &&
			/^(Enter|Escape|ArrowUp|ArrowDown)$/.test(e.code)
		) {
			e.preventDefault();
			e.stopPropagation();
			if (/^(Enter|ArrowUp|ArrowDown)$/.test(e.code)) {
				const children: Array<HTMLLIElement> = Array.from(
						this._autocompleteMenu.children,
					) as Array<HTMLLIElement>,
					selectedIndex = children.findIndex((i) => i.className.includes('selected'));
				if (/^(ArrowUp|ArrowDown)$/.test(e.code)) {
					children[selectedIndex].classList.remove('selected');
					let item = children[selectedIndex + (e.code == 'ArrowUp' ? -1 : 1)];
					if (!item) item = e.code == 'ArrowUp' ? children[children.length - 1] : children[0];
					item.classList.add('selected');
					const scrollY = this._autocompleteMenu.scrollTop;
					if (item.offsetTop < scrollY) {
						this._autocompleteMenu.scrollTop = item.offsetTop;
					} else if (
						item.offsetHeight + item.offsetTop >
						scrollY + this._autocompleteMenu.offsetHeight
					) {
						this._autocompleteMenu.scrollTop =
							item.offsetTop + item.offsetHeight - this._autocompleteMenu.offsetHeight;
					}
				} else if (selectedIndex >= 0) {
					this._dispatchEvent(children[selectedIndex] as HTMLLIElement);
				}
			}
			if (/^(Enter|Escape)$/.test(e.code)) this._hide();
		}
	};
	private _handleKeyUp = (e: KeyboardEvent) => {
		if (
			e.altKey ||
			e.ctrlKey ||
			/^(Enter|Escape|Control|Alt|ArrowUp|ArrowDown|Shift|Meta|F|Insert)/.test(e.code)
		) {
			if (!(e.ctrlKey && e.code == 'Space')) return;
		}
		this._hide();
		const value = this._textEditor.value,
			selectionStart = this._textEditor.selectionStart,
			lastLineStart = Math.max(0, value.slice(0, selectionStart).lastIndexOf('\n')),
			lastLine = value.slice(lastLineStart, selectionStart),
			lastWord = lastLine.match(/\b[\w._]+$/);
		if (lastWord) this._show(lastWord[0]);
	};
	private _onClickHandler = (e: PointerEvent) => {
		if (this._autocompleteMenu.classList.contains('show')) {
			e.preventDefault();
			this._hide();
			document.body.removeEventListener('mousedown', this._onClickHandler);
			this._dispatchEvent((<HTMLElement>e.target)?.closest('li.autocomplete-item'));
		}
	};
	private _dispatchEvent = (itemMenu: HTMLLIElement) => {
		if (itemMenu) {
			this.dispatchEvent(
				new CustomEvent('clickItem', {
					detail: { index: itemMenu['index'], text: itemMenu['event'] },
				}),
			);
		}
	};
	private _show(lastWord: string) {
		if (/^\W/.test(lastWord)) {
			if (/^["'`]/.test(lastWord)) return;
			lastWord = lastWord.slice(1);
			if (!lastWord) return;
		}
		let context: AutoCompleteOption[] = [],
			hightWord = lastWord,
			token = lastWord.split('.');
		if (/^(BLE|Script)$/.test(token[0])) {
			token[0] = `${token.shift()}.${token[0]}`;
		}
		if (lastWord.includes('.')) {
			context = this._getKeyWordsMembers(token[0]);
			if (context.length == 0) {
				const opt = this._analyseCode().filter((i) => i.title == token[0])[0];
				if (opt) {
					hightWord = token[1];
					context = this._analyseToken(opt).filter((i) => this._startsWith(token[1], i.title));
					if (token.length == 3 && token[2] != null) {
						const prop = {
							string: 'new String',
							number: 'new Number',
							array: 'new Array',
							inst: opt.params,
						};
						const returns = this._getKeyWordsProps(
							prop[opt.type] + '.' + token[1].replace(/\(.*\)$/, ''),
							/\(.*\)$/.test(token[1]) ? 'methods' : 'params',
						)?.r;
						if (returns) {
							hightWord = token[2];
							context = this._getKeyWordsMembers(
								prop[returns] || returns,
								false,
								token[0] + '.' + token[1],
							);
						}
					}
				}
			} else if (token[1]) {
				hightWord = token[1];
				context = context.filter((i) => this._startsWith(token[1], i.title));
			}
		} else {
			context = [...this._analyseCode(), ...getKeyWords()].filter((i) =>
				this._startsWith(lastWord, i.title),
			);
			if (context.length == 1) {
				const methods = this._getKeyWordsMembers(context[0].title, true);
				if (methods.length) context = methods;
			}
		}
		if (context.length == 0) return;
		const index = this._textEditor.selectionEnd - lastWord.length;
		for (let i = 0; i < context.length; ++i) {
			const optionEl = document.createElement('li');
			optionEl.className = `autocomplete-item${i == 0 ? ' selected' : ''}`;
			let title = context[i].title;
			const m = title.match(new RegExp(hightWord, 'i'));
			if (m) title = title.replace(m[0], `<span class="highlight">${m[0]}</span>`);
			optionEl.innerHTML = `<span>${title}</span><span class="icon">${context[i].icon}</span>`;
			optionEl['index'] = index;
			optionEl['event'] = context[i]?.event || context[i].title;
			this._autocompleteMenu.append(optionEl);
		}
		this._getCaretPosition();
		this._autocompleteMenu.classList.add('show');
		this._autocompleteMenu.scrollTop = 0;
		this._updateCaretPosition();
		window.addEventListener('resize', this._updateCaretPosition);
		document.body.addEventListener('mousedown', this._onClickHandler);
	}
	private _hide() {
		this._autocompleteMenu.classList.remove('show');
		window.removeEventListener('resize', this._updateCaretPosition);
		while (this._autocompleteMenu.firstChild) {
			this._autocompleteMenu.removeChild(this._autocompleteMenu.firstChild);
		}
	}
	private _getCaretPosition() {
		const value = this._textEditor.value,
			selectionEnd = this._textEditor.selectionEnd,
			beforeSelection = value.slice(0, selectionEnd),
			span = document.createElement('span');
		this._mirrorEditor.textContent = beforeSelection;
		this._mirrorEditor.appendChild(span);
		this._position.offsetX = span.offsetLeft;
		this._position.offsetY = span.offsetTop;
	}
	private _updateCaretPosition = () => {
		if (!this._autocompleteMenu.className.includes('show')) return;
		const pos = this._position,
			buffer = 5,
			rect = this._autocompleteMenu.getBoundingClientRect();
		let posX = pos.offsetX - pos.x,
			posY = pos.offsetY - pos.y + pos.lineHeight;
		if (posX + rect.width >= this._textEditor.clientWidth && posX - rect.width > 0)
			posX -= rect.width + buffer;
		if (posY - pos.lineHeight + rect.height + buffer > this._textEditor.clientHeight) {
			posY -= pos.lineHeight + rect.height + buffer;
		}
		this._autocompleteMenu.style.transform = `translate(${posX}px, ${posY}px)`;
	};
	private _analyseCode(): AutoCompleteOption[] {
		const hP = ShellyScriptHightlight.patterns,
			value = this._textEditor.value
				.replace(new RegExp(`${hP.fullcomment}|${hP.comment}`, 'g'), '')
				.replace(/\s+/g, ' '),
			members: AutoCompleteOption[] = [],
			s = `(\\s*?)`,
			m = `${s}([A-Za-z0-9_$]+)${s}`,
			vars = `\\b(let|var|const)${m}(=|;|\\n|$)*.`,
			startStr = `[^"'\`]`,
			patterns = {
				string: `${vars}${s}(${hP.string})`,
				methods: `\\b(function)${m}(\\([^(]*)\\)${s}\\{`,
				boolean: `${vars}${s}(${startStr}${hP.boolean})`,
				number: `${vars}${s}(${startStr}${hP.number})`,
				array: `${vars}${s}\\[((\\s*?.*?)*?)\\]`,
				object: `${vars}${s}\\{`,
				inst: `${vars}${s}(${[
					...getKeyWordsHighlight(true, true),
					...getKeyWordsHighlight(false, true),
				]
					.flat()
					.join('|')})`,
			};
		for (const p in patterns) {
			for (const v of value.matchAll(new RegExp(patterns[p], 'gm'))) {
				const fn = 'function',
					type = v[1],
					title = v[3],
					params = v[7],
					parent = title && this._searchParrentScope(v);
				if (!title || parent || members.find((i) => i.title == title)) continue;
				let returns = null;
				if (params && p == 'inst') {
					const props = this._getKeyWordsProps(params);
					returns = props?.methods || props?.params ? props : props?.r;
				}
				members.push({
					title,
					type: p,
					params,
					event: title + (type == fn ? '()' : ''),
					icon: type == fn ? AutoCompleteIcons.methods : AutoCompleteIcons.params,
					returns,
					parent,
					//v
				});
			}
		}
		return members;
	}
	private _startsWith(s1: string, s2: string): boolean {
		return s2
			.toString()
			.toLocaleLowerCase()
			.slice(0, -1)
			.startsWith(s1.toString().toLocaleLowerCase());
	}
	private _analyseToken(opt: AutoCompleteOption): AutoCompleteOption[] {
		const prop = {
				string: 'new String',
				number: 'new Number',
				array: 'new Array',
				inst: opt.params,
			},
			type = opt.returns || opt.type;
		if (opt.returns && typeof opt.returns == 'object') {
			return this._getKeyWordsMembers('', false, opt.title, opt.returns);
		} else if (prop[type]) {
			return this._getKeyWordsMembers(prop[type], false, opt.title);
		}
		return [];
	}
	private _searchParrentScope(v: RegExpMatchArray): string {
		const value = v.input,
			index = v.index,
			parentStart = value.slice(0, index).lastIndexOf('{'),
			parentEnd = value.slice(parentStart).indexOf('}'),
			lastIndex = parentStart + parentEnd + 1;
		if (parentStart >= 0 && parentEnd >= 0 && lastIndex > index) {
			return value.slice(parentStart, lastIndex);
		}
		return null;
	}
	private _getKeyWordsMembers(
		lastWord: string,
		includeMainKeyWord = false,
		lastWordAlias: string = null,
		member: any = null,
	): AutoCompleteOption[] {
		if (/^(methods|params)$/.test(lastWord)) return [];
		lastWordAlias = lastWordAlias || lastWord;
		const keywords = [];
		member = member || shellyKeyWords[lastWord] || jsKeyWords[lastWord];
		if (member) {
			for (const k in member?.params) {
				keywords.push({
					title: includeMainKeyWord ? `${lastWord}.${k}` : k,
					event: `${lastWordAlias}.${k}`,
					icon: shellyKeyWords[lastWord] ? AutoCompleteIcons.shelly : AutoCompleteIcons.params,
				});
			}
			for (const k in member.methods) {
				keywords.push({
					title: includeMainKeyWord ? `${lastWord}.${k}` : k,
					event: `${lastWordAlias}.${k}()`,
					icon: shellyKeyWords[lastWord]
						? AutoCompleteIcons.shelly
						: AutoCompleteIcons.methods,
				});
			}
		}
		return keywords;
	}
	private _getKeyWordsProps = (params: string, type: 'methods' | 'params' = null): any => {
		let words = params.split('.'),
			firstWord = words[0],
			secondWord = words[1];
		if (/^(BLE|Script)$/.test(firstWord)) {
			firstWord = `${firstWord}.${secondWord}`;
			secondWord = words[2];
		}
		const member = shellyKeyWords[firstWord] || jsKeyWords[firstWord];
		if (secondWord) {
			if (type) return member?.[type]?.[secondWord];
			return member?.methods?.[secondWord] || member?.params?.[secondWord];
		}
		return member;
	};
}
